import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';
import { useTheme } from '../hooks/useTheme';
import { getRegistrationDetails } from '../api/registration';
import { FaSignOutAlt, FaIdCard, FaCalendarAlt, FaCheckCircle, FaMapMarker } from 'react-icons/fa';
import Cookies from 'js-cookie';
import Notification from '../components/Notification';
import logoImage from '../assets/images/logo-jemari-juicy-luicy.jpg';
import { formatWIBDate } from '../utils/dateUtils';
import { generateMemberId } from '../utils/memberIdUtils';
import { getProvinceFromCityName } from '../utils/cityMappingUtils';
import MemberCardQR from '../components/MemberCardQR';
import { resolvePageContext } from '../utils/fanMemberUtils';
import { getFanMemberBrandTheme } from '../config/fanMembersConfig';

const MemberPage = ({ fixedActivityType, fixedId, fixedFanSlug }) => {
  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [error, setError] = useState(null);
  const { theme } = useTheme();
  const params = useParams();
  const pageContext = resolvePageContext({ fixedFanSlug, fixedActivityType, fixedId, params });
  const { fanMember, activityId, activityType, isFanMemberRoute, navigateTo } = pageContext;
  const navigate = useNavigate();
  const registrationId = Cookies.get('registrationId');
  const phoneNumber = Cookies.get('phoneNumber');
  const memberLogo = fanMember?.logo || logoImage;
  const brand = getFanMemberBrandTheme(fanMember);
  const isDark = theme === 'dark' || brand.surfaceMode === 'dark';

  const handleLogout = () => {
    Cookies.remove('registrationId');
    Cookies.remove('phoneNumber');

    if (isFanMemberRoute) {
      navigate(navigateTo());
    } else {
      navigate(`/${activityType}/${activityId}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!registrationId || !activityId) {
          setError('No registration ID or activity ID found, please register or login again');
          setTimeout(() => {
            if (isFanMemberRoute) {
              navigate(navigateTo());
            } else {
              navigate(`/${activityType}/${activityId}`);
            }
          }, 3000);
          return;
        }

        const registrationResponse = await getRegistrationDetails(registrationId, activityId);
        if (!registrationResponse.success || !registrationResponse.data.isVerified) {
          setError('Registration not verified, please verify your registration');
          setTimeout(() => {
            if (isFanMemberRoute) {
              navigate(navigateTo());
            } else {
              navigate(`/${activityType}/${activityId}`);
            }
          }, 3000);
          return;
        }

        // Generate member ID and get its parts
        const generatedMemberId = generateMemberId(registrationResponse.data);
        const cityVal = registrationResponse.data.formSubmissionValues?.find(value => value.formField.name === "Kota")?.value || "Unknown";
        const provinceFromForm = registrationResponse.data.formSubmissionValues?.find(
          (value) => value.formField.name === "Provinsi" || value.formField.name === "Province"
        )?.value;

		setMemberData({
          name: registrationResponse.data.user.name,
          memberId: generatedMemberId,
          memberPrefix: registrationResponse.data.activity.memberPrefix,
          memberNumber: fanMember?.activityScopedMemberNumber
            ? (registrationResponse.data.memberNumber || registrationResponse.data.id)
            : registrationResponse.data.id,
          picture: registrationResponse.data.formSubmissionValues?.filter(value => value.formField.name === "Profile Picture")[0]?.fileUrl || "https://via.placeholder.com/150",
          createdAt: registrationResponse.data.createdAt,
          activity: registrationResponse.data.activity,
          city: cityVal,
          province: provinceFromForm || getProvinceFromCityName(cityVal) || "",
          email: registrationResponse.data.user.email,
          phone: registrationResponse.data.user.phoneNumber,
          fanMember,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.response?.data?.error || 'Failed to fetch member data');
        setTimeout(() => {
          if (isFanMemberRoute) {
            navigate(navigateTo());
          } else {
            navigate(`/${activityType}/${activityId}`);
          }
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [registrationId, activityId, activityType, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <BeatLoader color={isDark ? brand.loaderDark : brand.loader} size={15} />
          <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Loading your member card...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-3 ${isDark ? brand.pageBgDark : brand.pageBg}`}>
      {error && (
        <Notification
          message={error}
          type="error"
          onClose={() => setError(null)}
        />
      )}

      <div className="max-w-lg mx-auto">
        {/* Balanced Header */}
        <div className="text-center mb-5 mt-5">
          <div className={`inline-flex items-center justify-center w-28 h-28 rounded-full ${brand.logoRing} mb-3 overflow-hidden`}>
            <img
              src={memberLogo}
              alt="Logo"
              className="w-full h-full object-cover rounded-full"
            />
          </div>
          <h1 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Member Card
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {memberData?.fanMember?.cardTitle || memberData?.activity.name}
          </p>
        </div>

        {/* Balanced Member Card */}
        <div className={`rounded-xl overflow-hidden shadow-lg mb-5 ${isDark
          ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700'
          : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200'
          }`}>

          {/* Header Strip */}
          <div className={`${brand.headerGradient} px-4 py-2.5`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div>
                  <h3 className="text-white font-bold text-sm">
                    {memberData?.fanMember?.cardTitle || memberData?.activity.name}
                  </h3>
                  <p className={`${brand.stripSubtitle} text-xs`}>
                    Official Member ID
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1 bg-green-500/20 px-2 py-1 rounded-full">
                <FaCheckCircle className="text-green-300 text-xs" />
                <span className="text-green-300 text-xs font-medium">VERIFIED</span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-4">
            {/* Profile Section */}
            <div className="flex gap-4 mb-4">
              {/* Left Side - Profile */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gradient-to-r from-blue-500 to-purple-600">
                    <img
                      src={memberData?.picture}
                      alt={memberData?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                </div>
              </div>

              {/* Right Side - Basic Info */}
              <div className="flex-1 min-w-0">
                <div>
                  <h2 className={`text-base font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {memberData?.name}
                  </h2>
                  <p className={`text-sm font-medium ${isDark ? brand.officialLabelDark : brand.officialLabel}`}>
                    {memberData?.fanMember?.officialMemberLabel || `Official Member ${memberData?.activity?.name || ''}`}
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    {memberData?.fanMember && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${isDark ? brand.badgeDark : brand.badge}`}>
                        {memberData.fanMember.shortLabel}
                      </span>
                    )}
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {memberData?.province}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Full Width Member ID */}
            <div className={`rounded-lg p-3 ${isDark
              ? 'bg-slate-800/50 border border-slate-700'
              : 'bg-gray-50 border border-gray-200'
              }`}>
              <div className="flex items-center justify-between mb-2">
                <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  MEMBER ID
                </label>
                <FaIdCard className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
              <div className={`text-xl font-mono font-bold tracking-wide ${isDark ? brand.memberIdDark : brand.memberId}`}>
                <span className={isDark ? brand.memberPrefixDark : brand.memberPrefix}>
                  {memberData?.memberPrefix}
                </span>
                {memberData?.memberId}
              </div>
            </div>

            {/* City */}
            <div className={`mt-4 rounded-lg p-3 ${isDark
              ? 'bg-slate-800/50 border border-slate-700'
              : 'bg-gray-50 border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FaMapMarker className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Kota
                  </span>
                </div>
                <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {memberData?.city}
                </div>
              </div>
            </div>

            {/* Member Since */}
            <div className={`mt-4 rounded-lg p-3 ${isDark
              ? 'bg-slate-800/50 border border-slate-700'
              : 'bg-gray-50 border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FaCalendarAlt className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Member Since
                  </span>
                </div>
                <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatWIBDate(memberData?.createdAt)}
                </div>
              </div>
            </div>

            {/* Shareable QR Code - Compact Version */}
            <div className="mt-4">
              <MemberCardQR
                phoneNumber={phoneNumber}
                activityType={activityType}
                activityId={activityId}
                fanMember={fanMember}
                theme={isDark ? 'dark' : 'light'}
              />
            </div>

            {/* Status Footer */}
            <div className="mt-3 pt-3 border-t border-opacity-20 border-gray-400">
              <div className="flex justify-between items-center">
                <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Digitally verified
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 font-medium ${isDark
            ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-600'
            : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-200'
            } shadow-md hover:shadow-lg`}
        >
          <FaSignOutAlt className="text-base" />
          <span>Sign Out</span>
        </button>

        {/* Footer */}
        <div className="text-center mt-5 pb-5">
          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            For support, contact your organization administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default MemberPage;